/* 2026-10 — Narrated sample player for the landing hero. Fetches the
 * admin-generated TTS sample (`GET /api/landing/audiobook-sample`) and
 * plays it with a small custom transport — no raw <audio> chrome, to
 * stay on-brand with the rest of the Scriptorium hero.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Play, Pause, Headphones } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { P, FONTS } from './palette';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

function fmtTime(sec) {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function AudiobookSamplePlayer() {
  const { t } = useTranslation();
  const [sample, setSample] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [current, setCurrent] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/landing/audiobook-sample`)
      .then((r) => { if (active) setSample(r.data); })
      .catch(() => { /* no sample configured yet — render nothing */ })
      .finally(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, []);

  if (!loaded || !sample) return null;

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
    } else {
      el.play().catch(() => {});
    }
  };

  const onTimeUpdate = () => {
    const el = audioRef.current;
    if (!el || !el.duration) return;
    setCurrent(el.currentTime);
    setProgress((el.currentTime / el.duration) * 100);
  };

  const onSeek = (e) => {
    const el = audioRef.current;
    if (!el || !el.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    el.currentTime = pct * el.duration;
  };

  return (
    <div
      className="mx-auto mt-5 flex max-w-xl items-center gap-4 rounded-2xl border px-5 py-4"
      style={{ borderColor: `${P.oxblood}33`, backgroundColor: `${P.paper}`, boxShadow: '0 2px 14px rgba(0,0,0,0.06)' }}
      data-testid="scriptorium-audiobook-sample"
    >
      <audio
        ref={audioRef}
        src={`${BACKEND}${sample.audio_url}`}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={onTimeUpdate}
      />
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? t('landing.audiobookPauseAria') : t('landing.audiobookPlayAria')}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-transform hover:scale-105"
        style={{ backgroundColor: P.oxblood, color: P.paper }}
        data-testid="audiobook-sample-play-button"
      >
        {playing ? <Pause className="h-4.5 w-4.5" /> : <Play className="h-4.5 w-4.5" />}
      </button>

      <div className="min-w-0 flex-1">
        <div
          className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em]"
          style={{ color: P.oxblood, fontFamily: FONTS.type }}
        >
          <Headphones className="h-3 w-3" />
          <span data-testid="audiobook-sample-voice-label">
            {t('landing.audiobookNarratedBy', { name: sample.voice_persona_name })}
          </span>
        </div>
        <div
          className="truncate text-[13.5px]"
          style={{ color: P.ink, fontFamily: FONTS.body }}
          data-testid="audiobook-sample-title"
        >
          {sample.book_title}{sample.pseudonym_name ? ` — ${sample.pseudonym_name}` : ''}
        </div>
        <div
          className="mt-1.5 h-1.5 w-full cursor-pointer rounded-full"
          style={{ backgroundColor: `${P.oxblood}1f` }}
          onClick={onSeek}
          data-testid="audiobook-sample-progress-bar"
        >
          <div
            className="h-full rounded-full transition-[width]"
            style={{ width: `${progress}%`, backgroundColor: P.oxblood }}
          />
        </div>
      </div>

      <div
        className="shrink-0 text-[11.5px] tabular-nums"
        style={{ color: P.inkSoft, fontFamily: FONTS.type }}
        data-testid="audiobook-sample-time"
      >
        {fmtTime(current)} / {fmtTime(sample.duration_sec)}
      </div>
    </div>
  );
}
